// Public-facing one-click payment page for admin-generated (manual) invoices.
// The invoice email contains a link like /pay/invoice/NDL-INV-... ?t=<token>
// which loads this page, shows the amount due, and launches PayFast on click.

import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CreditCard, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { lookupInvoiceForPayment } from "@/lib/api/invoice-payment.functions";
import { launchPayfastCheckout } from "@/lib/payfast-launch";
import { site, companyRegLine } from "@/lib/site";
import { z } from "zod";

const Search = z.object({
  t: z.string().optional(),
  payfast: z.enum(["return", "cancel"]).optional(),
});

export const Route = createFileRoute("/pay/invoice/$ref")({
  head: () => ({
    meta: [
      { title: "Pay invoice · Nkadimeng Danny Legal & Compliance Support" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  validateSearch: (s) => Search.parse(s),
  component: PayInvoicePage,
});

const rZAR = (c: number) =>
  `R${(c / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function PayInvoicePage() {
  const { ref } = useParams({ from: "/pay/invoice/$ref" });
  const search = useSearch({ from: "/pay/invoice/$ref" });
  const lookup = useServerFn(lookupInvoiceForPayment);
  const [launching, setLaunching] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["pay-invoice", ref, search.t],
    queryFn: () => lookup({ data: { reference: ref, token: search.t as string } }),
    enabled: !!search.t,
    retry: false,
  });

  async function pay() {
    setErr(null);
    setLaunching(true);
    try {
      await launchPayfastCheckout(ref);
    } catch (e: any) {
      setErr(e?.message || "Could not start PayFast");
      setLaunching(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <header className="mb-6 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Secure online payment</p>
        <h1 className="mt-1 font-display text-3xl text-navy">{site.name}</h1>
        <p className="text-[11px] text-muted-foreground">{companyRegLine()}</p>
      </header>

      {search.payfast === "return" ? (
        <Card className="mb-4 border-emerald-300 bg-emerald-50">
          <CardContent className="flex items-center gap-2 py-3 text-sm text-emerald-800">
            <CheckCircle2 className="h-4 w-4" /> Payment submitted. We'll confirm by email as soon as PayFast releases the funds.
          </CardContent>
        </Card>
      ) : null}
      {search.payfast === "cancel" ? (
        <Card className="mb-4 border-amber-300 bg-amber-50">
          <CardContent className="flex items-center gap-2 py-3 text-sm text-amber-800">
            <XCircle className="h-4 w-4" /> Payment cancelled — you can try again below.
          </CardContent>
        </Card>
      ) : null}

      {!search.t ? (
        <Card><CardContent className="py-6 text-sm text-rose-700">Invalid payment link — please use the secure link from your invoice email, or contact us to have it re-sent.</CardContent></Card>
      ) : isLoading ? (
        <p className="text-sm text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Loading invoice…</p>
      ) : error ? (
        <Card><CardContent className="py-6 text-sm text-rose-700">{(error as any)?.message}</CardContent></Card>
      ) : data ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="font-mono text-base">{data.reference}</span>
              <span className={`rounded px-2 py-0.5 text-xs ${data.status === "paid" ? "bg-emerald-100 text-emerald-800" : data.status === "cancelled" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                {String(data.status).toUpperCase()}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-1 text-sm">
              <div><span className="text-muted-foreground">Billed to:</span> <span className="font-medium text-navy">{data.client_name}</span></div>
              <div><span className="text-muted-foreground">Issued:</span> {data.issued_date}</div>
              {data.due_date ? <div><span className="text-muted-foreground">Due:</span> {data.due_date}</div> : null}
            </div>
            <div className="rounded-md border border-border bg-secondary/40 p-4 text-center">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Amount due</p>
              <p className="mt-1 font-display text-4xl text-navy">{rZAR(data.total_cents)}</p>
              {data.vat_cents > 0 ? (
                <p className="text-[11px] text-muted-foreground">Includes VAT (15%) of {rZAR(data.vat_cents)}</p>
              ) : null}
            </div>

            {data.status === "paid" ? (
              <div className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-800">
                This invoice has been settled — thank you.
              </div>
            ) : data.status === "cancelled" ? (
              <div className="rounded-md bg-rose-50 p-3 text-sm text-rose-800">
                This invoice was cancelled. Contact us if this is unexpected.
              </div>
            ) : (
              <>
                <Button size="lg" className="w-full" onClick={pay} disabled={launching}>
                  {launching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                  Pay {rZAR(data.total_cents)} securely with PayFast
                </Button>
                {err ? <p className="text-xs text-rose-700">{err}</p> : null}
                <p className="flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
                  <ShieldCheck className="h-3 w-3" /> Payments are processed by PayFast (South Africa). We never see your card details.
                </p>
                <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
                  <p className="mb-1 font-medium text-navy">Prefer EFT?</p>
                  {site.banking.bank} · {site.banking.accountName} · Acc {site.banking.accountNumber} · Branch {site.banking.branchCode}. Use <span className="font-mono">{data.reference}</span> as reference.
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ) : null}

      <p className="mt-6 text-center text-[11px] text-muted-foreground">
        Questions? Email {site.email} — quote invoice {ref}.
        {" "}<button className="underline" onClick={() => refetch()}>Refresh</button>
      </p>
    </div>
  );
}
